import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Smartphone, Save, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Switch } from '../ui/switch';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=az.net.fiber.texnik';
const APP_STORE_URL = '';

function VersionForm({ platform, label, icon: Icon, defaultStoreUrl, value, onSaved }) {
  const token = localStorage.getItem('token');
  const [minVersion, setMinVersion] = useState(value?.min_version || '1.0.0');
  const [currentVersion, setCurrentVersion] = useState(value?.current_version || '1.0.0');
  const [forceUpdate, setForceUpdate] = useState(value?.force_update ?? true);
  const [storeUrl, setStoreUrl] = useState(value?.store_url || defaultStoreUrl);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (value) {
      setMinVersion(value.min_version || '1.0.0');
      setCurrentVersion(value.current_version || '1.0.0');
      setForceUpdate(value.force_update ?? true);
      setStoreUrl(value.store_url || defaultStoreUrl);
    }
  }, [value, defaultStoreUrl]);

  const handleSave = async () => {
    if (!/^\d+\.\d+\.\d+$/.test(minVersion)) {
      toast.error('Minimum versiya formatı: x.y.z (məs: 1.6.0)');
      return;
    }
    if (currentVersion && !/^\d+\.\d+\.\d+$/.test(currentVersion)) {
      toast.error('Cari versiya formatı: x.y.z');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/app-version`, {
        platform,
        min_version: minVersion,
        current_version: currentVersion || null,
        force_update: forceUpdate,
        store_url: storeUrl || null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${label} versiya yeniləndi`);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Yadda saxlamaq mümkün olmadı');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid={`version-card-${platform}`}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Icon className="h-5 w-5 text-zinc-700" />
          {label}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <label className="text-sm font-medium text-zinc-700 mb-1 block">
            Minimum versiya <span className="text-red-500">*</span>
          </label>
          <Input
            value={minVersion}
            onChange={(e) => setMinVersion(e.target.value)}
            placeholder="1.6.0"
            data-testid={`min-version-${platform}`}
          />
          <p className="text-xs text-zinc-500 mt-1">
            Bu versiyadan aşağı app açılmasın (force update tətiqlənəcək)
          </p>
        </div>

        <div>
          <label className="text-sm font-medium text-zinc-700 mb-1 block">
            Cari (tövsiyə olunan) versiya
          </label>
          <Input
            value={currentVersion}
            onChange={(e) => setCurrentVersion(e.target.value)}
            placeholder="1.6.0"
            data-testid={`current-version-${platform}`}
          />
        </div>

        <div>
          <label className="text-sm font-medium text-zinc-700 mb-1 block">Store URL</label>
          <Input
            value={storeUrl}
            onChange={(e) => setStoreUrl(e.target.value)}
            placeholder={defaultStoreUrl || 'https://...'}
            data-testid={`store-url-${platform}`}
          />
        </div>

        <div className="flex items-center justify-between p-3 bg-zinc-50 rounded-md border">
          <div>
            <p className="text-sm font-medium text-zinc-900">Məcburi yeniləmə</p>
            <p className="text-xs text-zinc-500">Aktiv olduqda köhnə versiyalar bağlanır</p>
          </div>
          <Switch
            checked={forceUpdate}
            onCheckedChange={setForceUpdate}
            data-testid={`force-update-${platform}`}
          />
        </div>

        <Button
          onClick={handleSave}
          disabled={saving}
          className="w-full bg-purple-600 hover:bg-purple-700 text-white"
          data-testid={`save-version-${platform}`}
        >
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Saxlanılır...' : 'Yadda saxla'}
        </Button>

        {value?.updated_at && (
          <p className="text-xs text-zinc-400 text-center">
            Son yenilənmə: {new Date(value.updated_at).toLocaleString('az-AZ')}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

export default function AppVersionPanel() {
  const token = localStorage.getItem('token');
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchVersions = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/app-version`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setVersions(res.data || []);
    } catch (e) {
      toast.error('Versiya konfiqurasiyaları yüklənmədi');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchVersions(); }, [fetchVersions]);

  const androidConfig = versions.find(v => v.platform === 'android');
  const iosConfig = versions.find(v => v.platform === 'ios');

  return (
    <div className="space-y-6" data-testid="app-version-panel">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Smartphone className="h-5 w-5 text-purple-600" />
            Mobil App Versiya İdarəetməsi
          </CardTitle>
          <p className="text-sm text-zinc-500">
            Mobil app açılarkən bu versiya yoxlanır. Cihazdakı versiya minimumdan aşağıdırsa və
            "Məcburi yeniləmə" aktivdirsə, app store səhifəsinə yönləndirilir.
          </p>
        </CardHeader>
      </Card>

      <Card className="bg-amber-50 border-amber-200">
        <CardContent className="p-4 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            <p className="font-medium mb-1">Diqqət</p>
            <p>
              Minimum versiyanı yüksəltməzdən əvvəl yeni APK/IPA mütləq Store-da yayımlanmalıdır.
              Əks halda istifadəçilər app-a daxil ola bilməyəcəklər.
            </p>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <p className="text-zinc-500 text-center py-8">Yüklənir...</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <VersionForm
            platform="android"
            label="Android"
            icon={Smartphone}
            defaultStoreUrl={PLAY_STORE_URL}
            value={androidConfig}
            onSaved={fetchVersions}
          />
          <VersionForm
            platform="ios"
            label="iOS"
            icon={Smartphone}
            defaultStoreUrl={APP_STORE_URL}
            value={iosConfig}
            onSaved={fetchVersions}
          />
        </div>
      )}
    </div>
  );
}
